/**
 * Railway Dashboard Component
 *
 * Main dashboard for Railway instance management with polling support.
 */

import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { raxApi, type RailwayInstance } from "../../api/rax-api.js";
import { raxTheme, raxButtonStyles } from "../styles/rax-theme.js";
import "./instance-status-badge.js";

@customElement("railway-dashboard")
export class RailwayDashboard extends LitElement {
  static override styles = [
    raxTheme,
    raxButtonStyles,
    css`
      :host {
        display: block;
        padding: var(--space-6);
        max-width: 800px;
        margin: 0 auto;
      }
      
      .dashboard-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: var(--space-6);
      }
      
      .dashboard-title {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }
      
      .dashboard-title h1 {
        font-size: var(--font-2xl);
        font-weight: 700;
        margin: 0;
        color: var(--text-primary);
      }
      
      .dashboard-title .icon {
        font-size: 32px;
      }
      
      /* Instance Card */
      .instance-card {
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-xl);
        padding: var(--space-6);
      }
      
      .instance-card:hover {
        border-color: var(--rax-yellow);
      }
      
      .card-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: var(--space-5);
      }
      
      .card-title {
        font-size: var(--font-xl);
        font-weight: 600;
        color: var(--text-primary);
        margin: 0 0 var(--space-2) 0;
      }
      
      .card-subtitle {
        font-size: var(--font-sm);
        color: var(--text-secondary);
      }
      
      /* Instance Details Grid */
      .details-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: var(--space-4);
        margin-bottom: var(--space-5);
        padding: var(--space-4);
        background: var(--bg-elevated);
        border-radius: var(--radius-lg);
      }
      
      .detail-item {
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
      }
      
      .detail-label {
        font-size: 10px;
        color: var(--text-tertiary);
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      
      .detail-value {
        font-size: var(--font-sm);
        color: var(--text-primary);
        font-weight: 500;
        word-break: break-all;
      }
      
      .detail-value.mono {
        font-family: monospace;
        color: var(--rax-yellow);
      }
      
      .storage-warning {
        margin-bottom: var(--space-4);
        padding: var(--space-3) var(--space-4);
        border: 1px solid rgba(251, 191, 36, 0.4);
        border-radius: var(--radius-md);
        background: rgba(251, 191, 36, 0.08);
        color: var(--rax-yellow);
        font-size: var(--font-sm);
        line-height: 1.4;
      }
      
      /* Card Actions */
      .card-actions {
        display: flex;
        gap: var(--space-3);
        padding-top: var(--space-5);
        border-top: 1px solid var(--border-color);
      }
      
      .card-actions .btn {
        flex: 1;
      }
      
      /* Empty State */
      .empty-state {
        text-align: center;
        padding: 80px var(--space-5);
        background: var(--bg-secondary);
        border: 2px dashed var(--border-color);
        border-radius: var(--radius-xl);
      }
      
      .empty-icon {
        font-size: 64px;
        margin-bottom: var(--space-4);
      }
      
      .empty-state h3 {
        font-size: var(--font-xl);
        margin-bottom: var(--space-2);
        color: var(--text-primary);
      }
      
      .empty-state p {
        color: var(--text-secondary);
        margin-bottom: var(--space-6);
        max-width: 400px;
        margin-left: auto;
        margin-right: auto;
      }
      
      /* Provisioning State */
      .provisioning-content {
        text-align: center;
        padding: var(--space-6);
      }
      
      .provisioning-spinner {
        width: 48px;
        height: 48px;
        border: 4px solid var(--border-color);
        border-top-color: var(--rax-yellow);
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin: 0 auto var(--space-4);
      }
      
      @keyframes spin {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(360deg);
        }
      }
      
      .provisioning-text {
        font-size: var(--font-lg);
        color: var(--text-primary);
        margin-bottom: var(--space-2);
      }
      
      .provisioning-subtext {
        font-size: var(--font-sm);
        color: var(--text-secondary);
        margin-bottom: var(--space-5);
      }
      
      /* Error State */
      .error-content {
        text-align: center;
        padding: var(--space-6);
      }
      
      .error-icon {
        font-size: 48px;
        margin-bottom: var(--space-4);
      }
      
      .error-title {
        font-size: var(--font-xl);
        color: var(--error);
        margin-bottom: var(--space-2);
      }
      
      .error-message {
        font-size: var(--font-sm);
        color: var(--text-secondary);
        margin-bottom: var(--space-5);
        padding: var(--space-3);
        background: rgba(239, 68, 68, 0.1);
        border-radius: var(--radius-md);
        font-family: monospace;
      }
      
      /* Loading */
      .loading {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: var(--space-8);
      }
      
      .loading-spinner {
        width: 32px;
        height: 32px;
        border: 3px solid var(--border-color);
        border-top-color: var(--rax-yellow);
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }
      
      /* Button styles */
      .btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: var(--space-2);
        padding: var(--space-3) var(--space-5);
        border-radius: var(--radius-md);
        font-size: var(--font-sm);
        font-weight: 600;
        cursor: pointer;
        transition: all var(--transition-fast);
        border: none;
      }
      
      .btn-primary {
        background: var(--rax-yellow);
        color: var(--rax-black);
      }
      
      .btn-primary:hover:not(:disabled) {
        background: var(--rax-gold);
        box-shadow: var(--shadow-glow);
      }
      
      .btn-primary:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      .btn-secondary {
        background: var(--bg-elevated);
        color: var(--text-primary);
        border: 1px solid var(--border-color);
      }
      
      .btn-secondary:hover {
        background: var(--bg-hover);
        border-color: var(--text-tertiary);
      }
      
      .btn-danger {
        background: transparent;
        color: var(--error);
        border: 1px solid var(--error);
      }
      
      .btn-danger:hover {
        background: rgba(239, 68, 68, 0.1);
      }
      
      .btn-lg {
        padding: var(--space-4) var(--space-6);
        font-size: var(--font-base);
      }
    `,
  ];

  @state()
  private instance: RailwayInstance | null = null;

  @state()
  private loading = true;

  @state()
  private error: string | null = null;

  @state()
  private isPolling = false;

  private pollIntervalId: number | null = null;
  private previousStatus: string | null = null;

  override async connectedCallback() {
    super.connectedCallback();
    await this.fetchInstance();
    this.requestNotificationPermission();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.stopPolling();
  }

  override updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);

    // Start or stop polling based on instance status
    if (changedProperties.has("instance")) {
      const instance = this.instance;
      if (instance?.status === "provisioning" && !this.isPolling) {
        this.startPolling();
      } else if (instance?.status !== "provisioning" && this.isPolling) {
        this.stopPolling();
      }
    }
  }

  private async fetchInstance() {
    this.loading = true;
    this.error = null;

    try {
      const result = await raxApi.getInstance();
      if (result.success) {
        this.instance = result.data ?? null;
        this.previousStatus = this.instance?.status ?? null;
      } else {
        this.error = result.error || "Failed to fetch instance";
      }
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Network error";
    } finally {
      this.loading = false;
    }
  }

  private startPolling() {
    if (this.isPolling) return;

    this.isPolling = true;
    this.pollIntervalId = window.setInterval(() => {
      this.pollInstanceStatus();
    }, 3000);

    console.log("[RailwayDashboard] Started polling");
  }

  private stopPolling() {
    if (this.pollIntervalId !== null) {
      window.clearInterval(this.pollIntervalId);
      this.pollIntervalId = null;
    }
    this.isPolling = false;
    console.log("[RailwayDashboard] Stopped polling");
  }

  private async pollInstanceStatus() {
    try {
      const result = await raxApi.getInstance();
      if (result.success && result.data) {
        const newStatus = result.data.status;
        const oldStatus = this.previousStatus;

        this.instance = result.data;

        // Check for status transition to running
        if (oldStatus === "provisioning" && newStatus === "running") {
          this.showNotification("Your agent is ready! Click to open chat.");
          this.stopPolling();
        } else if (newStatus === "error") {
          this.stopPolling();
        }

        this.previousStatus = newStatus;
      }
    } catch (err) {
      console.error("[RailwayDashboard] Poll error:", err);
    }
  }

  private requestNotificationPermission() {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {
        // User denied or browser doesn't support
      });
    }
  }

  private showNotification(message: string) {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification("Rax", {
        body: message,
        icon: "/favicon.ico",
      });
    } else {
      console.log("[RailwayDashboard] Notification:", message);
    }
  }

  override render() {
    return html`
      <div class="dashboard-header">
        <div class="dashboard-title">
          <span class="icon">🚂</span>
          <h1>Railway Instance</h1>
        </div>
      </div>

      ${this.loading ? this.renderLoading() : ""}
      ${!this.loading && this.error ? this.renderError() : ""}
      ${!this.loading && !this.error && !this.instance ? this.renderEmpty() : ""}
      ${!this.loading && !this.error && this.instance ? this.renderInstance() : ""}
    `;
  }

  private renderLoading() {
    return html`
      <div class="loading">
        <div class="loading-spinner"></div>
      </div>
    `;
  }

  private renderEmpty() {
    return html`
      <div class="empty-state">
        <div class="empty-icon">🚂</div>
        <h3>No Instance Running</h3>
        <p>Create a Railway instance to run your AI agent in the cloud. Your agent will be available 24/7.</p>
        <button class="btn btn-primary btn-lg" @click=${this.handleCreateInstance}>
          Create Instance
        </button>
      </div>
    `;
  }

  private renderInstance() {
    const instance = this.instance!;

    switch (instance.status) {
      case "provisioning":
        return this.renderProvisioning();
      case "running":
        return this.renderRunning();
      case "error":
        return this.renderInstanceError();
      default:
        return this.renderStopped();
    }
  }

  private renderProvisioning() {
    return html`
      <div class="instance-card">
        <div class="provisioning-content">
          <div class="provisioning-spinner"></div>
          <instance-status-badge status="provisioning"></instance-status-badge>
          <p class="provisioning-text">Setting up your instance...</p>
          <p class="provisioning-subtext">
            This usually takes 1-2 minutes. We'll notify you when it's ready.
          </p>
          <button class="btn btn-primary" disabled>Open Chat</button>
        </div>
      </div>
    `;
  }

  private renderRunning() {
    const instance = this.instance!;
    const storage = instance.storage;
    const volumeId = instance.config?.railwayVolumeId ?? storage?.volumeId ?? null;
    const mountPath = instance.config?.railwayVolumeMountPath ?? storage?.mountPath ?? null;
    const durable = storage?.durable ?? Boolean(volumeId && mountPath);

    return html`
      <div class="instance-card">
        <div class="card-header">
          <div>
            <h2 class="card-title">Your Agent Instance</h2>
            <span class="card-subtitle">Running on Railway</span>
          </div>
          <instance-status-badge status="running"></instance-status-badge>
        </div>

        ${storage?.warning ? html`<div class="storage-warning">⚠️ ${storage.warning}</div>` : ""}

        <div class="details-grid">
          ${
            storage
              ? html`
                <div class="detail-item">
                  <span class="detail-label">Storage</span>
                  <span class="detail-value">
                    ${durable ? "Durable volume attached" : "Ephemeral (no durable volume confirmed)"}
                  </span>
                </div>
              `
              : ""
          }
          ${
            instance.url
              ? html`
            <div class="detail-item">
              <span class="detail-label">URL</span>
              <span class="detail-value mono">${instance.url}</span>
            </div>
          `
              : ""
          }
          ${
            instance.region
              ? html`
            <div class="detail-item">
              <span class="detail-label">Region</span>
              <span class="detail-value">${instance.region}</span>
            </div>
          `
              : ""
          }
          ${
            volumeId
              ? html`
            <div class="detail-item">
              <span class="detail-label">Volume ID</span>
              <span class="detail-value mono">${volumeId}</span>
            </div>
          `
              : ""
          }
          ${
            mountPath
              ? html`
            <div class="detail-item">
              <span class="detail-label">Volume Mount</span>
              <span class="detail-value mono">${mountPath}</span>
            </div>
          `
              : ""
          }
          <div class="detail-item">
            <span class="detail-label">Created</span>
            <span class="detail-value">${this.formatDate(instance.createdAt)}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Last Active</span>
            <span class="detail-value">${this.formatDate(instance.lastActiveAt)}</span>
          </div>
        </div>

        <div class="card-actions">
          <button class="btn btn-primary" @click=${this.handleOpenChat}>
            ⚡ Open Chat
          </button>
          <button class="btn btn-danger" @click=${this.handleDeleteInstance}>
            Delete Instance
          </button>
        </div>
      </div>
    `;
  }

  private renderInstanceError() {
    const instance = this.instance!;
    const errorMessage = instance.config?.errorMessage || "An unknown error occurred";

    return html`
      <div class="instance-card">
        <div class="error-content">
          <div class="error-icon">⚠️</div>
          <instance-status-badge status="error"></instance-status-badge>
          <h3 class="error-title">Instance Error</h3>
          <p class="error-message">${errorMessage}</p>
          <div class="card-actions" style="justify-content: center; border: none; padding-top: 0;">
            <button class="btn btn-secondary" @click=${this.handleContactSupport}>
              Contact Support
            </button>
            <button class="btn btn-danger" @click=${this.handleDeleteInstance}>
              Delete Instance
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderStopped() {
    const instance = this.instance!;
    const storage = instance.storage;
    const volumeId = instance.config?.railwayVolumeId ?? storage?.volumeId ?? null;
    const mountPath = instance.config?.railwayVolumeMountPath ?? storage?.mountPath ?? null;
    const durable = storage?.durable ?? Boolean(volumeId && mountPath);

    return html`
      <div class="instance-card">
        <div class="card-header">
          <div>
            <h2 class="card-title">Your Agent Instance</h2>
            <span class="card-subtitle">Instance is stopped</span>
          </div>
          <instance-status-badge status="${instance.status}"></instance-status-badge>
        </div>

        ${storage?.warning ? html`<div class="storage-warning">⚠️ ${storage.warning}</div>` : ""}

        <div class="details-grid">
          ${
            storage
              ? html`
                <div class="detail-item">
                  <span class="detail-label">Storage</span>
                  <span class="detail-value">
                    ${durable ? "Durable volume attached" : "Ephemeral (no durable volume confirmed)"}
                  </span>
                </div>
              `
              : ""
          }
          ${
            volumeId
              ? html`
            <div class="detail-item">
              <span class="detail-label">Volume ID</span>
              <span class="detail-value mono">${volumeId}</span>
            </div>
          `
              : ""
          }
          ${
            mountPath
              ? html`
            <div class="detail-item">
              <span class="detail-label">Volume Mount</span>
              <span class="detail-value mono">${mountPath}</span>
            </div>
          `
              : ""
          }
          <div class="detail-item">
            <span class="detail-label">Created</span>
            <span class="detail-value">${this.formatDate(instance.createdAt)}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Last Active</span>
            <span class="detail-value">${this.formatDate(instance.lastActiveAt)}</span>
          </div>
        </div>

        <div class="card-actions">
          <button class="btn btn-primary" @click=${this.handleWakeInstance}>
            Wake Instance
          </button>
          <button class="btn btn-danger" @click=${this.handleDeleteInstance}>
            Delete Instance
          </button>
        </div>
      </div>
    `;
  }

  private renderError() {
    return html`
      <div class="instance-card">
        <div class="error-content">
          <div class="error-icon">⚠️</div>
          <h3 class="error-title">Connection Error</h3>
          <p class="error-message">${this.error}</p>
          <button class="btn btn-secondary" @click=${this.fetchInstance}>
            Try Again
          </button>
        </div>
      </div>
    `;
  }

  private formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  }

  // Event Handlers

  private async handleCreateInstance() {
    this.loading = true;
    this.error = null;

    try {
      const result = await raxApi.createInstance();
      if (result.success && result.data) {
        this.instance = result.data;
        this.previousStatus = result.data.status;
        // Polling will start automatically via updated() lifecycle
      } else {
        this.error = result.error || "Failed to create instance";
      }
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Network error";
    } finally {
      this.loading = false;
    }
  }

  private handleOpenChat() {
    if (this.instance?.url) {
      window.open(this.instance.url, "_blank");
    }
  }

  private async handleDeleteInstance() {
    const confirmed = window.confirm(
      "Are you sure you want to delete this instance? This action cannot be undone.",
    );

    if (!confirmed) return;

    this.loading = true;
    this.error = null;

    try {
      const result = await raxApi.deleteInstance();
      if (result.success) {
        this.instance = null;
        this.previousStatus = null;
        this.stopPolling();
      } else {
        this.error = result.error || "Failed to delete instance";
      }
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Network error";
    } finally {
      this.loading = false;
    }
  }

  private async handleWakeInstance() {
    this.loading = true;
    this.error = null;

    try {
      const result = await raxApi.wakeInstance();
      if (result.success) {
        // Refetch to get updated status
        await this.fetchInstance();
      } else {
        this.error = result.error || "Failed to wake instance";
      }
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Network error";
    } finally {
      this.loading = false;
    }
  }

  private handleContactSupport() {
    const instance = this.instance;
    const errorContext = instance?.config?.errorMessage || "";
    const errorCode = instance?.config?.errorCode || "";

    const params = new URLSearchParams({
      subject: "Railway Instance Error",
      error: errorContext,
      code: errorCode,
      instanceId: instance?.id || "",
    });

    window.location.href = `/support?${params.toString()}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "railway-dashboard": RailwayDashboard;
  }
}
